import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import { fetchAllRecipes } from './api';
import Heading1 from './headings/Heading1';
import RecipeCard from './Recipe-card';

function toDate(obj) {
  if (!obj) return null;
  // Firestore timestamp
  if (obj.seconds) return new Date(obj.seconds * 1000);
  if (typeof obj.toDate === 'function') return obj.toDate();
  // ISO string or number
  const d = new Date(obj);
  if (!isNaN(d.getTime())) return d;
  return null;
}

export default function NewIn({ count = 6 }) {
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(3);
  const containerRef = useRef(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragDelta, setDragDelta] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    fetchAllRecipes()
      .then(list => {
        // sort by createdAt if available, otherwise keep order
        const sorted = [...list].sort((a, b) => {
          const da = toDate(a.createdAt);
          const db = toDate(b.createdAt);
          if (da && db) return db - da; // newest first
          return 0;
        });
        setItems(sorted.slice(0, count));
      })
      .catch(err => {
        console.error('NewIn fetch error', err);
      });
  }, [count]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) setCardsPerView(1);
      else if (window.innerWidth < 1024) setCardsPerView(2);
      else setCardsPerView(3);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const next = () => {
    setIndex(prev => (prev >= Math.max(0, items.length - cardsPerView) ? 0 : prev + 1));
  };
  const prev = () => {
    setIndex(prev => (prev <= 0 ? Math.max(0, items.length - cardsPerView) : prev - 1));
  };

  // Measure container width for drag calculations
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.clientWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Touch handlers
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setDragStartX(e.touches[0].clientX);
    setDragDelta(0);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const delta = e.touches[0].clientX - dragStartX;
    setDragDelta(delta);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    const threshold = Math.max(50, containerWidth * 0.15);
    if (Math.abs(dragDelta) > threshold) {
      if (dragDelta > 0) prev(); else next();
    }
    setDragDelta(0);
  };

  // Mouse drag support
  useEffect(() => {
    let moving = false;
    const onMouseMove = (e) => {
      if (!moving) return;
      const delta = e.clientX - dragStartX;
      setDragDelta(delta);
    };

    const onMouseUp = () => {
      if (!moving) return;
      moving = false;
      setIsDragging(false);
      const threshold = Math.max(50, containerWidth * 0.15);
      if (Math.abs(dragDelta) > threshold) {
        if (dragDelta > 0) prev(); else next();
      }
      setDragDelta(0);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    if (isDragging) {
      moving = true;
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragStartX, dragDelta, containerWidth]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragDelta(0);
  };

  if (!items.length) return null;

  const maxIndex = Math.max(0, items.length - cardsPerView);

  return (
    <section className="w-full py-8">
        
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
            <Heading1
        title="New In"
      ></Heading1>
          <div className="flex gap-2">
            <button onClick={prev} aria-label="Previous" className="p-2 bg-white rounded-full shadow">
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button onClick={next} aria-label="Next" className="p-2 bg-white rounded-full shadow">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>

        <div
          ref={containerRef}
          className={`overflow-hidden ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
        >
          <div className="flex gap-6 transition-transform duration-500 ease-out" style={{
            transform: (() => {
              const basePercent = index * (100 / cardsPerView);
              const offsetPercent = containerWidth ? (dragDelta * 100) / containerWidth : 0;
              return `translateX(calc(-${basePercent}% + ${offsetPercent}%))`;
            })()
          }}>
            {items.map((r, i) => (
              <div key={r.id || i} className="flex-shrink-0" style={{ width: cardsPerView === 1 ? '100%' : cardsPerView === 2 ? 'calc(50% - 12px)' : 'calc(33.333% - 16px)' }}>
                <div className="flex justify-center">
                  <RecipeCard recipe={r} />
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Navigation indicators */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="flex justify-center items-center gap-3">
            {[...Array(maxIndex + 1)].map((_, i) => {
              const isActive = i === index;
              return (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  aria-label={`Go to group ${i + 1}`}
                  className={`transition-all duration-200 focus:outline-none ${isActive ? 'w-14 h-3 bg-pink-600 rounded-full shadow-lg' : 'w-3 h-3 bg-gray-300 rounded-full hover:scale-110'}`}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
